import { Input } from '@/modules/Input/components/Input';
import { DropdownProps, DropdownRef, DropdownOption } from '@/shared/types/dropdown';
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { warnDeprecatedDropdownFlatPropsIfNeeded } from '../utils/warnDeprecatedDropdownFlatProps';
import { createPortal } from 'react-dom';
import { ListBase, ListItem, ListItemWithCheckbox } from '@/modules/Lists';
import { cn } from '@/shared/utils/cn';
import { ListItemDefault } from '@/modules/Lists/components/molecules/ListItemDefault';
import { ListItemRadio } from '@/modules/Lists/components/molecules/ListItemRadio';
import { ListItemToggle } from '@/modules/Lists/components/molecules/ListItemToggle';
import { ListItemUser } from '@/modules/Lists/components/molecules/ListItemUser';
import { ListItemCheckbox } from '@/modules/Lists/components/molecules/ListItemCheckbox';
import { useDropdown } from '../hooks/useDropdown';
import {
  mergeGroupedDropdownProps,
  pickInputPassthroughProps,
} from '../utils/mergeGroupedDropdownProps';
import { IconFontColor, VisualSlotType } from '@/shared/types';
import { Avatar } from '@/modules/Avatar';
import { Button } from '@/modules/Button';
import { Icon } from '@/modules/Icon';
import { Tooltip } from '@/modules/Tooltip';
import { TagsMultiple } from './organisms/TagsMultiple';
import {
  faChevronUp,
  faChevronDown,
  faSearch,
  faCircleNotch,
} from '@fortawesome/pro-regular-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';

/**
 * Dropdown - Componente de dropdown/select versátil y configurable
 *
 * Componente de dropdown que soporta búsqueda, selección múltiple, diferentes variantes de lista,
 * estados controlados/no controlados, y múltiples tipos de opciones (default, checkbox, radio, toggle, user).
 *
 * **Ancho del menú:** el panel de la lista usa por defecto el ancho del trigger como `width` y
 * `maxWidth` (no supera el campo). `popover.fullWidth` por defecto es `true` (campo a ancho completo
 * del contenedor).
 *
 * @example
 * ```tsx
 * // Dropdown simple
 * <Dropdown
 *   label="Seleccionar opción"
 *   options={[
 *     { value: '1', label: 'Opción 1' },
 *     { value: '2', label: 'Opción 2' }
 *   ]}
 *   onValueChange={(value) => console.log(value)}
 * />
 *
 * // Dropdown con búsqueda
 * <Dropdown
 *   label="Buscar"
 *   searchable
 *   options={options}
 *   onValueChange={handleChange}
 * />
 *
 * // Dropdown con selección múltiple
 * <Dropdown
 *   label="Seleccionar múltiples"
 *   variant="checkbox"
 *   options={options}
 *   onCheckboxChange={handleMultiSelect}
 * />
 *
 * // Panel plazas/OBP — API agrupada (`list` + `multi` + `items` + `menuSearch`)
 * <Dropdown
 *   items={{ options }}
 *   multi={{ multiple: true }}
 *   list={{
 *     listVariant: 'search-multi-checkbox',
 *     searchMultiCheckbox: {
 *       search: { placeholder: 'Buscar…' },
 *       header: { id: 'all', label: 'Todas las plazas' },
 *       showClearSelection: true,
 *     },
 *   }}
 *   menuSearch={{ searchable: false }}
 *   events={{ onValueChange: () => {} }}
 * />
 * ```
 *
 * @see {@link DropdownProps} para la lista completa de props
 * @see {@link DropdownRef} para métodos de control programático
 */
export const Dropdown = forwardRef<DropdownRef, DropdownProps>((props, ref) => {
  const dropdownDeprecationSigRef = useRef('');
  warnDeprecatedDropdownFlatPropsIfNeeded(props, dropdownDeprecationSigRef);
  const merged = mergeGroupedDropdownProps(props);
  const inputPassthrough = pickInputPassthroughProps(props);
  const name = props.name;

  const {
    label,
    error,
    helperText,
    infoTooltip,
    size,
    color,
    leftSlot,
    rightSlot,
    searchPlaceholder,
    noResultsText,
    noResultsDescription,
    caret,
    value,
    defaultValue,
    disabled,
    placeholder,
    options,
    valueName,
    idName,
    onValueChange,
    onOptionSelect,
    onCheckboxChange,
    searchable,
    maxHeight,
    closeOnClickOutside,
    dropdownId,
    multiple,
    maxSelectedItems,
    selectionSummary,
    absolute,
    popoverZIndex,
    hasCurrent,
    footer,
    defaultOptionSelected,
    withCheckbox,
    checkboxSelectionTone,
    checkboxPosition,
    checkboxListDensity,
    onHandleAccept,
    pill,
    listVariant,
    searchMultiCheckbox,
    searchMultiCheckboxHeader,
    searchMultiCheckboxShowSearch,
    searchMultiCheckboxShowClearSelection,
    onToggleChange,
    filtersTitle,
    tooltipProps,
    loading,
    tagsOverflow,
    tagsOverflowPopoverPlacement,
    tagsOverflowPopoverZIndex,
    tagsOverflowPopoverTitle,
    preventDeselectFromOptionsList,
    showTriggerClearButton,
    userHideAvatars,
    fullWidth,
  } = merged;
  const customInputRef = useRef<HTMLDivElement | null>(null);

  const [currentLeftSlot, setCurrentLeftSlot] = useState<VisualSlotType | null>(leftSlot);
  const [currentIconColor, setCurrentIconColor] = useState<IconFontColor | null>(null);
  const [isOpening, setIsOpening] = useState(false);

  // Agregar opción por defecto siempre que esté habilitada
  const shouldShowDefaultOption = !multiple && defaultOptionSelected;

  const optionsWithDefault = useMemo(() => {
    const defaultOption: DropdownOption = {
      label: placeholder || 'Selecciona una opción',
      value: -1,
      disabled: false,
    };
    return multiple
      ? options
      : shouldShowDefaultOption
        ? [
          {
            ...defaultOption,
            [idName]: -1,
            value: -1,
            label: placeholder || 'Selecciona una opción',
          },
          ...options.filter((option) => option[idName] !== -1),
        ]
        : options;
  }, [multiple, shouldShowDefaultOption, options, idName, placeholder]);

  const effectiveWithCheckbox = withCheckbox || listVariant === 'search-multi-checkbox';

  // Establecer valor por defecto si no hay valor inicial y defaultOptionSelected es true
  const initialValue =
    value !== undefined ? value : defaultOptionSelected && !multiple ? -1 : defaultValue;

  const {
    // Estado
    isOpen,
    isClosing,
    searchValue,
    selectedValue,
    selectedOptions,
    filteredOptions,
    position,
    dropdownStyle,
    isControlled,
    multiple: isMultiple,

    // Referencias
    inputRef: hookInputRef,
    dropdownRef,
    containerRef,
    dropdownUniqueId,

    // Funciones
    toggleDropdown,
    closeDropdown,
    handleOptionSelect,
    handleSearchChange,
    handleClear,
    clearSearch,
    calculatePosition,
    scrollToSelectedOption,
    setValue,
    normalizeToArray,
  } = useDropdown({
    value,
    defaultValue: initialValue,
    options: optionsWithDefault,
    valueName,
    idName,
    onValueChange,
    onOptionSelect,
    searchable,
    closeOnClickOutside,
    dropdownId,
    disabled,
    multiple,
    maxSelectedItems,
    absolute,
    popoverZIndex,
    withCheckbox: effectiveWithCheckbox,
    onOpenChange: (isOpen) => {
      if (isOpen) {
        // Resetear isOpening cuando el dropdown esté completamente abierto
        setTimeout(() => {
          setIsOpening(false);
        }, 200); // Esperar a que termine la animación
      }
    },
    preventDeselectFromOptionsList,
  });

  // Función que maneja los cambios de estado de los checkboxes en un dropdown con selección múltiple
  const onHandleCheckboxChange = (option: DropdownOption, checked: boolean) => {
    if (multiple) {
      const currentValues = normalizeToArray(selectedValue);
      const optionValue = option[idName] as string | number;

      if (checked) {
        // Agregar la opción si no está ya seleccionada
        if (!currentValues.includes(optionValue)) {
          const newValues = [...currentValues, optionValue];
          setValue(newValues);
        }
      } else {
        const newValues = currentValues.filter((v) => v !== optionValue);
        setValue(newValues);
      }
    }
  };

  // Usar la referencia correcta según el modo
  const inputRef = multiple && selectionSummary === 'tags' ? customInputRef : hookInputRef;

  // Función para comparar valores de manera flexible (maneja strings y números)
  const compareValues = useCallback((val1: string | number, val2: string | number): boolean => {
    // Si ambos son iguales estrictamente, retornar true
    if (val1 === val2) return true;

    // Convertir ambos a string y comparar
    if (String(val1) === String(val2)) return true;

    // Convertir ambos a número y comparar (si ambos son números válidos)
    const num1 = Number(val1);
    const num2 = Number(val2);
    if (!isNaN(num1) && !isNaN(num2) && num1 === num2) return true;

    return false;
  }, []);

  // Resetear iconos cuando cambie el value externo (para componentes controlados)
  useEffect(() => {
    if (isControlled && value !== undefined) {
      // Si el valor está vacío o es -1 (opción por defecto), resetear iconos
      if (value === '' || value === -1 || value === '-1') {
        setCurrentLeftSlot((prev) => (prev !== leftSlot ? leftSlot : prev));
        setCurrentIconColor((prev) => (prev !== null ? null : prev));
      } else {
        const valueToCompare = Array.isArray(value) ? value[0] : value;
        const selectedOption = optionsWithDefault.find((option) => {
          if (valueToCompare === undefined || valueToCompare === null) return false;
          return (
            compareValues(option[valueName] as string | number, valueToCompare) ||
            compareValues(option[idName] as string | number, valueToCompare)
          );
        });
        if (selectedOption) {
          const newIcon = selectedOption.icon || leftSlot;
          const newIconColor = selectedOption.iconColor || null;

          setCurrentLeftSlot((prev) => (prev !== newIcon ? newIcon : prev));
          setCurrentIconColor((prev) => (prev !== newIconColor ? newIconColor : prev));

          if (listVariant === 'user') {
            setCurrentLeftSlot(
              selectedOption.avatarUrl ? (selectedOption.avatarUrl as VisualSlotType) : leftSlot,
            );
          }
        }
      }
    }
  }, [
    value,
    isControlled,
    optionsWithDefault,
    valueName,
    idName,
    leftSlot,
    compareValues,
    listVariant,
  ]);

  // Exponer métodos a través de ref
  useImperativeHandle(
    ref,
    () => ({
      getValue: () => selectedValue,
      setValue: (newValue: string | number | (string | number)[]) => {
        if (!isControlled) {
          setValue(newValue);
        }
        onValueChange?.(newValue);
      },
      focus: () => {
        if (multiple && selectionSummary === 'tags') {
          customInputRef.current?.focus();
        } else {
          hookInputRef.current?.focus();
        }
      },
      blur: () => {
        if (multiple && selectionSummary === 'tags') {
          customInputRef.current?.blur();
        } else {
          hookInputRef.current?.blur();
        }
      },
      clear: () => {
        if (!isControlled) {
          setValue(multiple ? [] : '');
        }
        onValueChange?.(multiple ? [] : '');
      },
      open: () => {
        if (!disabled) {
          calculatePosition();
          setTimeout(() => {
            toggleDropdown();
          }, 50);
        }
      },
      close: () => {
        closeDropdown();
      },
    }),
    [
      selectedValue,
      isControlled,
      disabled,
      calculatePosition,
      onValueChange,
      closeDropdown,
      multiple,
      setValue,
      toggleDropdown,
      hookInputRef,
      selectionSummary,
    ],
  );

  // Efecto para hacer scroll hasta la opción seleccionada cuando se abra el dropdown
  useEffect(() => {
    if (isOpen && selectedOptions.length > 0) {
      // Pequeño delay para asegurar que el DOM esté completamente renderizado
      const timer = setTimeout(() => {
        scrollToSelectedOption();
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [isOpen, selectedOptions, scrollToSelectedOption]);

  // Efecto para recalcular posición cuando cambie la altura del input (para modo tags)
  useEffect(() => {
    if (isOpen && multiple && selectionSummary === 'tags') {
      // Usar ResizeObserver para detectar cambios en la altura del input
      const inputElement = customInputRef.current;
      if (!inputElement) return;

      const resizeObserver = new ResizeObserver(() => {
        // Pequeño delay para asegurar que el DOM esté actualizado
        setTimeout(() => {
          calculatePosition();
        }, 50);
      });

      resizeObserver.observe(inputElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [isOpen, multiple, selectionSummary, calculatePosition]);

  // Efecto para recalcular posición cuando cambien las opciones seleccionadas (para modo tags)
  useEffect(() => {
    if (isOpen && multiple && selectionSummary === 'tags') {
      // Pequeño delay para asegurar que el DOM se haya actualizado con los nuevos tags
      const timer = setTimeout(() => {
        calculatePosition();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [selectedOptions, isOpen, multiple, selectionSummary, calculatePosition]);

  // Función para remover un elemento seleccionado (para múltiple)
  const handleRemoveSelected = (optionValue: string | number) => {
    if (multiple) {
      const currentValues = normalizeToArray(selectedValue);
      const newValues = currentValues.filter((v) => v !== optionValue);
      setValue(newValues);

      // Si withCheckbox está habilitado, también actualizar el estado de la opción
      if (effectiveWithCheckbox && onHandleCheckboxChange) {
        // BUscamos la opción correspondiente
        const option = options.find((opt) => opt[idName] === optionValue);
        if (option) {
          // Ejecutamos onHandleCheckboxChange para deseleccionar la opción
          onHandleCheckboxChange(option, false);
        }
      }
    }
  };

  /** Vacía la selección múltiple (footer «Limpiar selección» en la lista del menú). */
  const handleClearAllSelections = useCallback(() => {
    if (!multiple) return;
    const previous = [...selectedOptions];
    setValue([]);
    if (onCheckboxChange && effectiveWithCheckbox) {
      previous.forEach((opt) => {
        if (opt) onCheckboxChange(opt, false);
      });
    }
  }, [multiple, setValue, onCheckboxChange, effectiveWithCheckbox, selectedOptions]);

  const hasClearableSelection = useMemo(() => {
    if (disabled || !showTriggerClearButton) return false;
    if (multiple) {
      return selectedOptions.length > 0;
    }
    const opt = selectedOptions[0];
    if (!opt) return false;
    if (opt[idName] === -1 || opt[valueName] === -1 || opt[valueName] === '-1') return false;
    return true;
  }, [disabled, showTriggerClearButton, multiple, selectedOptions, idName, valueName]);

  const handleClearFromControl = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (multiple) {
        handleClearAllSelections();
        clearSearch();
      } else {
        handleClear(e);
        setCurrentLeftSlot(leftSlot ?? null);
        setCurrentIconColor(null);
      }
    },
    [multiple, handleClearAllSelections, clearSearch, handleClear, leftSlot],
  );

  // Función para obtener el texto de visualización del input
  const getDisplayValue = () => {
    if (multiple) {
      if (selectedOptions.length === 0) return '';

      switch (selectionSummary) {
        case 'count':
          return `${selectedOptions.length} elemento${selectedOptions.length > 1 ? 's' : ''} seleccionado${selectedOptions.length > 1 ? 's' : ''}`;
        case 'text':
          return selectedOptions
            .map((option) => option?.[valueName])
            .filter(Boolean)
            .join(', ');
        case 'tags':
        default:
          // Para modo tags, retornar cadena vacía ya que los tags se muestran dentro del input
          return '';
      }
    } else {
      const selectedOption = selectedOptions[0];
      if (!selectedOption) return '';

      // Si la opción seleccionada es la opción por defecto, mostrar el placeholder
      if (
        selectedOption[idName] === -1 ||
        selectedOption[valueName] === -1 ||
        selectedOption[valueName] === '-1'
      ) {
        return placeholder;
      }

      return String(selectedOption[valueName] || '');
    }
  };

  // Renderizar el dropdown
  const renderDropdown = () => {
    if (!isOpen || !dropdownStyle.top) return null;

    const animationClass = isClosing ? 'animate-dropdown-exit' : 'animate-dropdown-enter';

    if (listVariant === 'search-multi-checkbox' && multiple) {
      const smc = searchMultiCheckbox;
      const effectiveSearchMultiShowSearch =
        smc?.search?.show ?? searchMultiCheckboxShowSearch ?? true;
      const effectiveListSearchPlaceholder = smc?.search?.placeholder ?? searchPlaceholder;
      const effectiveSearchMultiHeader = smc?.header ?? searchMultiCheckboxHeader;
      const effectiveShowClearSelection =
        smc?.showClearSelection ?? searchMultiCheckboxShowClearSelection ?? true;

      const panelOpts = optionsWithDefault.filter(
        (o) =>
          !o.isGroupTitle &&
          (o[idName] as number) !== -1 &&
          o[idName] !== '-1' &&
          String(o[idName]) !== '-1',
      );
      const currentVals = normalizeToArray(selectedValue);
      const listItems = panelOpts.map((o) => ({
        id: String(o[idName]),
        label: String(o[valueName] ?? ''),
        checked: currentVals.some((v) => compareValues(v, o[idName] as string | number)),
        disabled: o.disabled,
      }));
      const nChecked = listItems.filter((i) => i.checked).length;
      const headerRow = {
        id: effectiveSearchMultiHeader?.id ?? 'all',
        label: effectiveSearchMultiHeader?.label ?? 'Todas las plazas',
        checked: nChecked === listItems.length && listItems.length > 0,
        indeterminate: nChecked > 0 && nChecked < listItems.length,
      };

      const handleHeaderCheck = (checked: boolean) => {
        if (!checked) {
          setValue([]);
          return;
        }
        const eligible = panelOpts
          .filter((o) => !o.disabled)
          .map((o) => o[idName] as string | number);
        if (maxSelectedItems != null && eligible.length > maxSelectedItems) {
          setValue(eligible.slice(0, maxSelectedItems));
        } else {
          setValue(eligible);
        }
      };

      const searchMultiContent = (
        <div
          ref={dropdownRef}
          id={dropdownUniqueId}
          data-dropdown-id={dropdownUniqueId}
          data-dropdown-portal="true"
          style={
            {
              position: dropdownStyle.position as 'fixed' | 'absolute',
              top: dropdownStyle.top,
              left: dropdownStyle.left,
              width: dropdownStyle.width,
              maxWidth: dropdownStyle.maxWidth,
              boxSizing: dropdownStyle.boxSizing,
              zIndex: dropdownStyle.zIndex,
              '--dropdown-z-index': dropdownStyle.zIndex,
            } as React.CSSProperties
          }
          className={cn(
            'ui-dropdown-portal ui-flex ui-min-w-0 ui-max-w-full ui-flex-col ui-overflow-hidden ui-rounded-surface-card ui-border ui-border-default ui-bg-fill ui-shadow-03',
            animationClass,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <ListBase
            variant="search-multi-checkbox"
            search={{
              show: effectiveSearchMultiShowSearch,
              placeholder: effectiveListSearchPlaceholder,
            }}
            list={{ header: headerRow, items: listItems }}
            events={{
              onHeaderCheckboxChange: handleHeaderCheck,
              onItemCheckboxChange: (itemId, checked) => {
                const opt = panelOpts.find((o) => String(o[idName]) === String(itemId));
                if (opt) onHandleCheckboxChange(opt, checked);
              },
            }}
            className="ui-max-h-[176px] ui-min-h-0 ui-rounded-none ui-border-0 ui-bg-transparent ui-shadow-none"
          />
          {effectiveShowClearSelection ? (
            <div className="ui-shrink-0 ui-p-8">
              <Button
                color="destructive"
                leftSlot={faTrash}
                variant="outlined"
                size="xs"
                fullWidth
                disabled={disabled || selectedOptions.length === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAllSelections();
                }}
              >
                Limpiar selección
              </Button>
            </div>
          ) : null}
          {footer && (
            <div className="ui-flex ui-min-w-0 ui-w-full ui-shrink-0 ui-gap-x-2 ui-p-8">
              <Button
                color="secondary"
                leftSlot={faTrash}
                variant="outlined"
                size="xs"
                fullWidth
                onClick={handleClear}
              >
                Limpiar
              </Button>
              <Button color="primary" size="xs" fullWidth onClick={() => closeDropdown()}>
                OK
              </Button>
            </div>
          )}
        </div>
      );

      return absolute ? searchMultiContent : createPortal(searchMultiContent, document.body);
    }

    const handleSelect = (option: DropdownOption, e?: React.MouseEvent) => {
      handleOptionSelect(option, e);

      if (option.icon) {
        setCurrentLeftSlot(option.icon as VisualSlotType);
      }

      if (option.iconColor) {
        setCurrentIconColor(option.iconColor as IconFontColor);
      }

      if (listVariant === 'user') {
        setCurrentLeftSlot(option.avatarUrl ? (option.avatarUrl as VisualSlotType) : leftSlot);
      }
    };

    const dropdownContent = (
      <div
        ref={dropdownRef}
        id={dropdownUniqueId}
        data-dropdown-id={dropdownUniqueId}
        data-dropdown-portal="true"
        style={
          {
            position: dropdownStyle.position as 'fixed' | 'absolute',
            top: dropdownStyle.top,
            left: dropdownStyle.left,
            width: dropdownStyle.width,
            maxWidth: dropdownStyle.maxWidth,
            boxSizing: dropdownStyle.boxSizing,
            zIndex: dropdownStyle.zIndex,
            '--dropdown-z-index': dropdownStyle.zIndex,
          } as React.CSSProperties
        }
        className={`ui-dropdown-portal ui-min-w-0 ui-max-w-full ui-overflow-hidden ui-rounded-surface-card ui-border ui-border-default ui-bg-fill ui-shadow-03 ${animationClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ui-w-full">
          {searchable && (
            <div className="ui-p-8" onClick={(e) => e.stopPropagation()}>
              <Input
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                leftSlot={faSearch}
                autoFocus
                fullWidth
                size="sm"
              />
            </div>
          )}
        </div>
        <div
          className={cn(
            'custom-scrollbar ui-flex ui-max-h-[176px] ui-w-full ui-flex-col ui-overflow-y-auto ui-px-8 ui-pb-8 ui-will-change-transform',
            searchable ? 'ui-pt-0' : 'ui-pt-8',
            withCheckbox && checkboxListDensity === 'compact' ? 'ui-gap-4' : 'ui-gap-8',
          )}
        >
          {/* Título para variante 'filters' */}
          {listVariant === 'filters' && filtersTitle && (
            <div className="ui-text-body-sm-regular ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ui-text-primary">
              {filtersTitle}
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              // Determinar si esta opción está seleccionada
              const isSelected = multiple
                ? selectedOptions.some(
                  (selected) =>
                    selected &&
                      compareValues(
                        selected[idName] as string | number,
                        option[idName] as string | number,
                      ),
                )
                : ((option[idName] as number) === -1 &&
                    (selectedValue === -1 || selectedValue === '-1')) ||
                  (Array.isArray(selectedValue)
                    ? false
                    : compareValues(option[idName] as string | number, selectedValue));

              // Si withCheckbox está habilitado, usar ListItemWithCheckbox
              if (withCheckbox) {
                return (
                  <ListItemWithCheckbox
                    key={String(
                      (option[idName] as string | number | undefined) ||
                        (option[valueName] as string | number | undefined),
                    )}
                    disabled={option.disabled}
                    current={isSelected ? 'blue' : false}
                    data-option-value={String(option[idName] as string | number)}
                    icon={option.icon as VisualSlotType | undefined}
                    iconColor={option.iconColor}
                    avatarUrl={option.avatarUrl as string | undefined}
                    checkboxSelectionTone={checkboxSelectionTone}
                    checkboxPosition={checkboxPosition}
                    compact={checkboxListDensity === 'compact'}
                    checkboxProps={{
                      checked: isSelected,
                      onChange: (checked) => onHandleCheckboxChange?.(option, checked),
                      disabled: option.disabled,
                    }}
                    isGroupTitle={option.isGroupTitle as boolean | undefined}
                    groupTitle={option.label}
                    hasCurrent={hasCurrent}
                  >
                    {String(option[valueName] || '')}
                  </ListItemWithCheckbox>
                );
              }

              const optionKey = String(
                (option[idName] as string | number | undefined) ||
                  (option[valueName] as string | number | undefined),
              );

              // Renderizar según listVariant
              switch (listVariant) {
                case 'list-default':
                  return (
                    <ListItemDefault
                      key={optionKey}
                      onClick={(e) => handleSelect(option, e)}
                      disabled={option.disabled}
                      current={isSelected}
                      color={option.color || 'blue'}
                      icon={option.icon as VisualSlotType | undefined}
                      iconColor={option.iconColor}
                    >
                      {String(option[valueName] || '')}
                    </ListItemDefault>
                  );

                case 'radio':
                  return (
                    <ListItemRadio
                      key={optionKey}
                      onClick={() => handleSelect(option, {} as React.MouseEvent)}
                      disabled={option.disabled}
                      selected={isSelected}
                      rightContent={option.rightContent as React.ReactNode | undefined}
                    >
                      {String(option[valueName] || '')}
                    </ListItemRadio>
                  );

                case 'toggle':
                  return (
                    <ListItemToggle
                      key={optionKey}
                      title={String(option[valueName] || '')}
                      description={option.description}
                      checked={option.checked ?? false}
                      disabled={option.disabled}
                      onChange={(checked) => onToggleChange?.(option, checked)}
                    />
                  );

                case 'user': {
                  const showAvatarColumn =
                    option.showAvatar === true ||
                    (!userHideAvatars && option.hideAvatar !== true);

                  let userListAvatar: React.ReactNode = null;
                  if (showAvatarColumn) {
                    const rawUrl = option.avatarUrl;
                    const hasAvatarUrl =
                      typeof rawUrl === 'string' && rawUrl.trim() !== '';
                    userListAvatar = hasAvatarUrl ? (
                      <Avatar type="people" src={rawUrl} size="sm" pill />
                    ) : option.avatar !== null && option.avatar !== undefined ? (
                      option.avatar
                    ) : (
                      <Avatar type="people" size="sm" pill />
                    );
                  }
                  return (
                    <ListItemUser
                      key={optionKey}
                      name={String(option[valueName] || '')}
                      subtitle={option.subtitle}
                      avatar={userListAvatar}
                      badge={option.badge}
                      badgeVariant={option.badgeVariant}
                      selected={isSelected}
                      onClick={() => handleSelect(option, {} as React.MouseEvent)}
                    />
                  );
                }

                case 'checkbox-item':
                  return (
                    <ListItemCheckbox
                      key={optionKey}
                      title={String(option[valueName] || '')}
                      description={option.description}
                      checked={option.checked ?? isSelected}
                      disabled={option.disabled}
                      onChange={(checked) => onToggleChange?.(option, checked)}
                    />
                  );

                case 'filters':
                  return (
                    <ListItemDefault
                      key={optionKey}
                      onClick={(e) => handleSelect(option, e)}
                      disabled={option.disabled}
                      current={isSelected}
                      color={option.color || 'blue'}
                      icon={option.icon as VisualSlotType | undefined}
                      iconColor={option.iconColor}
                    >
                      {String(option[valueName] || '')}
                    </ListItemDefault>
                  );

                case 'default':
                default:
                  // Comportamiento original con ListItem
                  return (
                    <ListItem
                      key={optionKey}
                      onClick={(e) => handleSelect(option, e)}
                      disabled={option.disabled}
                      current={isSelected ? 'blue' : false}
                      data-option-value={String(option[idName] as string | number)}
                      icon={option.icon as VisualSlotType | undefined}
                      iconColor={option.iconColor}
                    >
                      {String(option[valueName] || '')}
                    </ListItem>
                  );
              }
            })
          ) : searchValue ? (
            <div className="ui-mx-auto ui-max-w-[300px] ui-break-words ui-text-center ui-text-sm ui-text-neutral-500">
              <div>
                <Icon name={faSearch} size="sm" color="tertiary" />
              </div>
              <p className="ui-font-semibold ui-text-primary">
                {noResultsText} "{searchValue}"
              </p>
              <p className="ui-mt-2 ui-text-body-xs-regular ui-text-tertiary">
                {noResultsDescription}
              </p>
            </div>
          ) : (
            <div className="ui-text-center ui-text-sm ui-text-tertiary">Sin opciones</div>
          )}
        </div>
        {multiple && (
          <div className="ui-p-8">
            <Button
              color="destructive"
              leftSlot={faTrash}
              variant="outlined"
              size="xs"
              fullWidth
              disabled={disabled || selectedOptions.length === 0}
              onClick={(e) => {
                e.stopPropagation();
                handleClearAllSelections();
              }}
            >
              Limpiar selección
            </Button>
          </div>
        )}
        {footer && (
          <div className="ui-flex ui-min-w-0 ui-w-full ui-gap-x-2 ui-p-8">
            <Button
              color="secondary"
              leftSlot={faTrash}
              variant="outlined"
              size="xs"
              fullWidth
              onClick={handleClear}
            >
              Limpiar
            </Button>
            <Button color="primary" size="xs" fullWidth onClick={() => closeDropdown()}>
              OK
            </Button>
          </div>
        )}
      </div>
    );

    // Usar portal solo cuando absolute=false (comportamiento original)
    return absolute ? dropdownContent : createPortal(dropdownContent, document.body);
  };

  useEffect(() => {
    if (leftSlot) {
      setCurrentLeftSlot(leftSlot);
    }
  }, [leftSlot]);

  const dropdownContainer = (
    <div
      ref={containerRef}
      className={cn('ui-imj ui-relative', fullWidth ? 'ui-w-full' : 'ui-w-fit')}
      data-dropdown-container-id={dropdownUniqueId}
    >
      {multiple && selectionSummary === 'tags' ? (
        <TagsMultiple
          fullWidth={fullWidth}
          label={label}
          size={size}
          placeholder={placeholder}
          selectedOptions={selectedOptions}
          handleRemoveSelected={handleRemoveSelected}
          toggleDropdown={toggleDropdown}
          isOpen={isOpen}
          isOpening={isOpening}
          setIsOpening={setIsOpening}
          customInputRef={customInputRef}
          disabled={disabled}
          error={error}
          helperText={helperText || ''}
          leftSlot={loading ? faCircleNotch : (currentLeftSlot ?? null)}
          leftSlotClassName={loading ? 'ui-animate-spin animate-spin' : undefined}
          colorLeftIcon={loading ? undefined : currentIconColor || undefined}
          rightSlot={rightSlot}
          valueName={valueName}
          idName={idName}
          tagsOverflow={tagsOverflow}
          tagsOverflowPopoverPlacement={tagsOverflowPopoverPlacement}
          overflowPopoverZIndex={
            tagsOverflowPopoverZIndex ?? (popoverZIndex !== undefined ? popoverZIndex + 50 : 1150)
          }
          tagsOverflowPopoverTitle={tagsOverflowPopoverTitle}
          showClearButton={showTriggerClearButton}
          onClearAll={handleClearFromControl}
        />
      ) : (
        // Input normal para otros modos
        // `{...props}` primero: atributos DOM del consumidor; después las props del Dropdown para que
        // nunca se pisen `extraSlot`, `clearable` ni `onClear` (si no, `useInputSlots` cree que no hay chevron y la X queda mal posicionada).
        <Input
          ref={hookInputRef}
          {...inputPassthrough}
          label={label}
          error={error}
          helperText={helperText}
          infoTooltip={infoTooltip}
          size={size}
          color={color}
          leftSlot={loading ? faCircleNotch : (currentLeftSlot ?? undefined)}
          leftSlotClassName={loading ? 'ui-animate-spin animate-spin' : undefined}
          colorLeftIcon={loading ? undefined : currentIconColor || undefined}
          rightSlot={rightSlot}
          fullWidth={fullWidth}
          caret={caret}
          disabled={disabled}
          placeholder={placeholder}
          value={getDisplayValue()}
          readOnly
          onClick={(e) => toggleDropdown(e)}
          onClickExtraSlot={() => toggleDropdown()}
          name={name}
          pill={pill}
          autosize
          extraSlot={isOpen ? faChevronUp : faChevronDown}
          clearable={hasClearableSelection}
          onClear={handleClearFromControl}
        />
      )}

      {renderDropdown()}
    </div>
  );

  if (tooltipProps?.label) {
    return (
      <Tooltip
        label={tooltipProps.label}
        position={tooltipProps.position}
        alignment={tooltipProps.alignment}
        delay={tooltipProps.delay}
        disabled={tooltipProps.disabled}
        className={tooltipProps.className}
        absolute={tooltipProps.absolute}
        fullWidth
      >
        {dropdownContainer}
      </Tooltip>
    );
  }

  return dropdownContainer;
});

Dropdown.displayName = 'Dropdown';

/*
Ejemplo de uso con opción por defecto:

<Dropdown
    options={[
        { label: 'Opción 1', value: 1 },
        { label: 'Opción 2', value: 2 },
        { label: 'Opción 3', value: 3 }
    ]}
    placeholder="Selecciona una opción"
    defaultOptionSelected={true}
/>

Esto creará automáticamente una opción por defecto con:
- value: -1
- label: "Selecciona una opción" (o el texto del placeholder)
- selected: true (si defaultOptionSelected es true)
*/

/*
Ejemplo de uso del Dropdown con checkboxes:

const optionsWithCheckbox = [
  { id: '1', label: 'Opción 1', checked: false },
  { id: '2', label: 'Opción 2', checked: true },
  { id: '3', label: 'Opción 3', checked: false, disabled: true },
]

const handleCheckboxChange = (option: DropdownOption, checked: boolean) => {
  console.log(`Opción ${option.label} cambió a ${checked}`)
  // Aquí puedes actualizar el estado de las opciones
}

<Dropdown
  options={optionsWithCheckbox}
  withCheckbox={true}
  onCheckboxChange={handleCheckboxChange}
  placeholder="Selecciona opciones"
  multiple={true}
  selectionSummary="tags"
/>
*/
